import React, {
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  useTranslation,
} from 'react-i18next';


const DEFAULT_POSITION = {
  lat: 35.19879267098201,
  lng: 128.68738105465292,
};


function normalizeLanguage(
  value,
) {
  const language =
    String(
      value ||
      'ko',
    )
      .trim()
      .toLowerCase()
      .split('-')[0];

  return (
    language ||
    'ko'
  );
}


function loadGoogleMaps(
  language,
) {
  if (
    window.google?.maps
  ) {
    return Promise.resolve(
      window.google,
    );
  }

  const existingScript =
    document.querySelector(
      'script[src*="maps.googleapis.com/maps/api/js"]',
    );

  if (
    existingScript
  ) {
    return new Promise(
      (
        resolve,
        reject,
      ) => {
        const checkLoaded =
          () => {
            if (
              window.google?.maps
            ) {
              resolve(
                window.google,
              );

              return;
            }

            reject(
              new Error(
                'GOOGLE_MAPS_LOAD_FAILED',
              ),
            );
          };

        existingScript.addEventListener(
          'load',
          checkLoaded,
          {
            once:
              true,
          },
        );

        existingScript.addEventListener(
          'error',
          () => {
            reject(
              new Error(
                'GOOGLE_MAPS_LOAD_FAILED',
              ),
            );
          },
          {
            once:
              true,
          },
        );

        window.setTimeout(
          () => {
            if (
              window.google?.maps
            ) {
              resolve(
                window.google,
              );
            }
          },
          100,
        );
      },
    );
  }

  const apiKey =
    String(
      process.env
        .REACT_APP_GOOGLE_MAPS_API_KEY ||
      '',
    ).trim();

  if (
    !apiKey
  ) {
    return Promise.reject(
      new Error(
        'GOOGLE_MAPS_API_KEY_MISSING',
      ),
    );
  }

  return new Promise(
    (
      resolve,
      reject,
    ) => {
      const script =
        document.createElement(
          'script',
        );

      const locale =
        normalizeLanguage(
          language,
        );

      script.src =
        `https://maps.googleapis.com/maps/api/js` +
        `?key=${encodeURIComponent(
          apiKey,
        )}` +
        `&libraries=places` +
        `&language=${encodeURIComponent(
          locale,
        )}`;

      script.async =
        true;

      script.defer =
        true;

      script.onload =
        () => {
          if (
            window.google?.maps
          ) {
            resolve(
              window.google,
            );

            return;
          }

          reject(
            new Error(
              'GOOGLE_MAPS_LOAD_FAILED',
            ),
          );
        };

      script.onerror =
        () => {
          reject(
            new Error(
              'GOOGLE_MAPS_LOAD_FAILED',
            ),
          );
        };

      document.head.appendChild(
        script,
      );
    },
  );
}


function getCountry(
  addressComponents,
) {
  if (
    !Array.isArray(
      addressComponents,
    )
  ) {
    return '';
  }

  const component =
    addressComponents.find(
      (
        item,
      ) =>
        Array.isArray(
          item.types,
        ) &&
        item.types.includes(
          'country',
        ),
    );

  return (
    component?.short_name ||
    ''
  );
}


function createPosition(
  location,
) {
  if (
    !location
  ) {
    return null;
  }

  const lat =
    typeof location.lat ===
    'function'
      ? location.lat()
      : Number(
          location.lat,
        );

  const lng =
    typeof location.lng ===
    'function'
      ? location.lng()
      : Number(
          location.lng,
        );

  if (
    !Number.isFinite(
      lat,
    ) ||
    !Number.isFinite(
      lng,
    )
  ) {
    return null;
  }

  return {
    lat,
    lng,
  };
}


function createMapInfo(
  result,
  fallbackPosition,
) {
  const position =
    createPosition(
      result?.geometry?.location,
    ) ||
    createPosition(
      fallbackPosition,
    );

  return {
    address:
      result?.formatted_address ||
      result?.name ||
      '',

    country:
      getCountry(
        result?.address_components,
      ),

    position,

    placeID:
      result?.place_id ||
      '',
  };
}


export default function GoogleAddressPicker({
  open,
  initialAddress,
  onClose,
  onSelect,
}) {
  const {
    t,
    i18n,
  } = useTranslation(
    'app',
  );

  const mapElementRef =
    useRef(
      null,
    );

  const searchInputRef =
    useRef(
      null,
    );

  const mapRef =
    useRef(
      null,
    );

  const markerRef =
    useRef(
      null,
    );

  const geocoderRef =
    useRef(
      null,
    );

  const mapClickListenerRef =
    useRef(
      null,
    );

  const autocompleteListenerRef =
    useRef(
      null,
    );

  const [
    selectedInfo,
    setSelectedInfo,
  ] = useState(
    null,
  );

  const [
    isLoading,
    setIsLoading,
  ] = useState(
    false,
  );

  const [
    loadError,
    setLoadError,
  ] = useState(
    false,
  );


  useEffect(
    () => {
      if (
        !open
      ) {
        return undefined;
      }

      let cancelled =
        false;

      setIsLoading(
        true,
      );

      setLoadError(
        false,
      );

      setSelectedInfo(
        null,
      );

      const initialize =
        async () => {
          try {
            await loadGoogleMaps(
              i18n.resolvedLanguage ||
              i18n.language,
            );

            if (
              cancelled ||
              !mapElementRef.current
            ) {
              return;
            }

            const google =
              window.google;

            const map =
              new google.maps.Map(
                mapElementRef.current,
                {
                  center:
                    DEFAULT_POSITION,

                  zoom:
                    16,

                  gestureHandling: 
                    'greedy',

                  mapTypeControl:
                    false,

                  streetViewControl:
                    false,

                  fullscreenControl:
                    false,
                },
              );

            const marker =
              new google.maps.Marker({
                map,

                position:
                  DEFAULT_POSITION,
              });

            const geocoder =
              new google.maps.Geocoder();

            mapRef.current =
              map;

            markerRef.current =
              marker;

            geocoderRef.current =
              geocoder;


            const applyResult =
              (
                result,
                fallbackPosition,
              ) => {
                const mapInfo =
                  createMapInfo(
                    result,
                    fallbackPosition,
                  );

                if (
                  !mapInfo.position
                ) {
                  return;
                }

                marker.setPosition(
                  mapInfo.position,
                );

                map.setCenter(
                  mapInfo.position,
                );

                setSelectedInfo(
                  mapInfo,
                );
              };


            const geocodePosition =
              (
                position,
              ) => {
                geocoder.geocode(
                  {
                    location:
                      position,
                  },
                  (
                    results,
                    status,
                  ) => {
                    if (
                      status !==
                        'OK' ||
                      !Array.isArray(
                        results,
                      ) ||
                      results.length ===
                        0
                    ) {
                      setSelectedInfo({
                        address:
                          '',

                        country:
                          '',

                        position:
                          createPosition(
                            position,
                          ),

                        placeID:
                          '',
                      });

                      marker.setPosition(
                        position,
                      );

                      return;
                    }

                    applyResult(
                      results[0],
                      position,
                    );
                  },
                );
              };


            mapClickListenerRef.current =
              map.addListener(
                'click',
                (
                  event,
                ) => {
                  const position =
                    createPosition(
                      event.latLng,
                    );

                  if (
                    !position
                  ) {
                    return;
                  }

                  marker.setPosition(
                    position,
                  );

                  geocodePosition(
                    position,
                  );
                },
              );


            if (
              searchInputRef.current &&
              google.maps.places
            ) {
              const autocomplete =
                new google.maps.places.Autocomplete(
                  searchInputRef.current,
                  {
                    fields: [
                      'address_components',
                      'formatted_address',
                      'geometry',
                      'name',
                      'place_id',
                    ],
                  },
                );

              autocomplete.bindTo(
                'bounds',
                map,
              );

              autocompleteListenerRef.current =
                autocomplete.addListener(
                  'place_changed',
                  () => {
                    const place =
                      autocomplete.getPlace();

                    const position =
                      createPosition(
                        place?.geometry
                          ?.location,
                      );

                    if (
                      !position
                    ) {
                      return;
                    }

                    const mapInfo =
                      createMapInfo(
                        place,
                        position,
                      );

                    marker.setPosition(
                      position,
                    );

                    map.setCenter(
                      position,
                    );

                    map.setZoom(
                      17,
                    );

                    setSelectedInfo(
                      mapInfo,
                    );
                  },
                );
            }


            const normalizedInitialAddress =
              String(
                initialAddress ||
                '',
              ).trim();

            if (
              normalizedInitialAddress
            ) {
              geocoder.geocode(
                {
                  address:
                    normalizedInitialAddress,
                },
                (
                  results,
                  status,
                ) => {
                  if (
                    status ===
                      'OK' &&
                    Array.isArray(
                      results,
                    ) &&
                    results.length >
                      0
                  ) {
                    const result =
                      results[0];

                    const position =
                      createPosition(
                        result.geometry
                          ?.location,
                      );

                    if (
                      position
                    ) {
                      marker.setPosition(
                        position,
                      );

                      map.setCenter(
                        position,
                      );

                      map.setZoom(
                        17,
                      );

                      setSelectedInfo(
                        createMapInfo(
                          result,
                          position,
                        ),
                      );
                    }
                  }
                },
              );
            } else {
              geocodePosition(
                DEFAULT_POSITION,
              );
            }
          } catch (
            error
          ) {
            if (
              !cancelled
            ) {
              setLoadError(
                true,
              );
            }
          } finally {
            if (
              !cancelled
            ) {
              setIsLoading(
                false,
              );
            }
          }
        };


      initialize();


      return () => {
        cancelled =
          true;

        if (
          mapClickListenerRef.current
        ) {
          window.google?.maps
            ?.event
            ?.removeListener(
              mapClickListenerRef.current,
            );

          mapClickListenerRef.current =
            null;
        }

        if (
          autocompleteListenerRef.current
        ) {
          window.google?.maps
            ?.event
            ?.removeListener(
              autocompleteListenerRef.current,
            );

          autocompleteListenerRef.current =
            null;
        }

        mapRef.current =
          null;

        markerRef.current =
          null;

        geocoderRef.current =
          null;
      };
    },
    [
      open,
      initialAddress,
      i18n.language,
      i18n.resolvedLanguage,
    ],
  );


  if (
    !open
  ) {
    return null;
  }


  const handleConfirm =
    () => {
      if (
        !selectedInfo?.position
      ) {
        return;
      }

      onSelect({
        address:
          selectedInfo.address ||
          '',

        country:
          selectedInfo.country ||
          '',

        position: {
          lat:
            Number(
              selectedInfo.position.lat,
            ),

          lng:
            Number(
              selectedInfo.position.lng,
            ),
        },

        placeID:
          selectedInfo.placeID ||
          '',
      });
    };


  return (
    <div
      style={{
        position:
          'fixed',

        inset:
          0,

        zIndex:
          10000,

        display:
          'flex',

        alignItems:
          'center',

        justifyContent:
          'center',

        padding:
          20,

        background:
          'rgba(0, 0, 0, 0.45)',
      }}
      onMouseDown={(
        event,
      ) => {
        if (
          event.target ===
          event.currentTarget
        ) {
          onClose();
        }
      }}
    >
      <div
        style={{
          width:
            'min(900px, 100%)',

          maxHeight:
            'calc(100vh - 40px)',

          overflow:
            'hidden',

          display:
            'flex',

          flexDirection:
            'column',

          background:
            '#ffffff',

          borderRadius:
            12,

          boxShadow:
            '0 18px 50px rgba(0, 0, 0, 0.2)',
        }}
      >
        <div
          style={{
            display:
              'flex',

            alignItems:
              'center',

            justifyContent:
              'space-between',

            padding:
              '18px 20px',

            borderBottom:
              '1px solid #e5e5e5',
          }}
        >
          <strong>
            {t(
              'signup.addressPicker.title',
            )}
          </strong>

          <button
            type="button"
            onClick={
              onClose
            }
            style={{
              border:
                0,

              background:
                'transparent',

              fontSize:
                22,

              cursor:
                'pointer',
            }}
          >
            ×
          </button>
        </div>


        <div
          style={{
            padding:
              16,
          }}
        >
          <input
            ref={
              searchInputRef
            }
            type="text"
            placeholder={t(
              'signup.addressPicker.searchPlaceholder',
            )}
            style={{
              width:
                '100%',

              height:
                42,

              boxSizing:
                'border-box',

              padding:
                '0 12px',

              border:
                '1px solid #d9d9d9',

              borderRadius:
                6,

              outline:
                'none',
            }}
          />
        </div>


        {
          loadError && (
            <div
              style={{
                padding:
                  '10px 20px 20px',

                color:
                  '#d32f2f',
              }}
            >
              {t(
                'signup.addressPicker.loadFailed',
              )}
            </div>
          )
        }


        <div
          ref={
            mapElementRef
          }
          style={{
            width:
              '100%',

            height:
              480,

            minHeight:
              320,

            background:
              '#eeeeee',
          }}
        />


        <div
          style={{
            padding:
              20,

            borderTop:
              '1px solid #e5e5e5',
          }}
        >
          <div
            style={{
              display:
                'grid',

              gap:
                8,

              marginBottom:
                18,

              fontSize:
                13,
            }}
          >
            <div>
              <strong>
                {t(
                  'signup.addressPicker.address',
                )}
              </strong>
              {' : '}
              {
                selectedInfo
                  ?.address ||
                '-'
              }
            </div>

            <div>
              <strong>
                {t(
                  'signup.addressPicker.country',
                )}
              </strong>
              {' : '}
              {
                selectedInfo
                  ?.country ||
                '-'
              }
            </div>

            <div>
              <strong>
                {t(
                  'signup.addressPicker.position',
                )}
              </strong>
              {' : '}
              {
                selectedInfo
                  ?.position
                  ? JSON.stringify(
                      selectedInfo.position,
                    )
                  : '-'
              }
            </div>
          </div>


          <div
            style={{
              display:
                'flex',

              justifyContent:
                'flex-end',

              gap:
                10,
            }}
          >
            <button
              type="button"
              onClick={
                onClose
              }
              style={{
                minWidth:
                  90,

                height:
                  40,

                border:
                  '1px solid #d9d9d9',

                borderRadius:
                  6,

                background:
                  '#ffffff',

                cursor:
                  'pointer',
              }}
            >
              {t(
                'signup.addressPicker.cancel',
              )}
            </button>

            <button
              type="button"
              onClick={
                handleConfirm
              }
              disabled={
                isLoading ||
                !selectedInfo
                  ?.position
              }
              style={{
                minWidth:
                  90,

                height:
                  40,

                border:
                  0,

                borderRadius:
                  6,

                background:
                  '#f37321',

                color:
                  '#ffffff',

                fontWeight:
                  700,

                cursor:
                  'pointer',
              }}
            >
              {t(
                'signup.addressPicker.confirm',
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}